import {Button, IconButton} from '@dropbox/dig-components/buttons';
import {Chip} from '@dropbox/dig-components/chip';
import {Checkbox} from '@dropbox/dig-components/controls';
import {Menu} from '@dropbox/dig-components/menu';
import {Popover} from '@dropbox/dig-components/popover';
import {TextInput} from '@dropbox/dig-components/text_fields';
import {Tooltip} from '@dropbox/dig-components/tooltips';
import {Text} from '@dropbox/dig-components/typography';
import {atoms, Box, Split, Stack} from '@dropbox/dig-foundations';
import {UIIcon} from '@dropbox/dig-icons';
import {
  AddLine,
  CheckmarkLine,
  ChevronDownLine,
  DeleteLine,
  EditLine,
  LockLine,
  MetricsLine,
  MilestoneLine,
} from '@dropbox/dig-icons/assets';
import {pulseUserAtom} from 'atoms/auth';
import {EmployeeRoleLevel, WorkstreamMetric} from 'client';
import {CancelButton} from 'components/DSYS/CancelButton';
import {parseAndExtractTextFromLexicalJson} from 'components/DSYS/editor/utils/lexical';
import {RichTextArea} from 'components/DSYS/RichTextArea';
import {CURRENT_QUARTER, CURRENT_YEAR} from 'constant';
import {isUserAdmin} from 'helpers/utils';
import {isMetricDraftReasonWaived} from 'helpers/workstreamDraftPolicy';
import {useOutsideClick} from 'hooks/useOutsideClick';
import {useWorkstreamDraftPolicy} from 'hooks/useWorkstreamDraftPolicy';
import {t} from 'i18next';
import {useAtomValue} from 'jotai';
import {
  type ComponentProps,
  type Ref,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';

import {useMnpiAccess} from './hooks';
import {DatePickerEditor, EmployeeSelect} from './TODODSYSStuff';
import {
  getMetricCreatedAtForDraftPolicy,
  getMetricOrder,
  getMetricWorkstreamLink,
  getSoonestMetricDate,
  getSoonestMetricTarget,
  isMetricLongterm,
  isPrimaryMetric,
} from './utils';

type WorkstreamMetricWithCurrentLink = WorkstreamMetric & {
  workstream_metric_link?: {
    id?: number | null;
    order?: number | null;
    is_primary?: boolean | null;
  } | null;
};

const PopoverContent = Popover.Content as (
  props: ComponentProps<typeof Popover.Content> & {ref?: Ref<HTMLDivElement>}
  // eslint-disable-next-line deprecation/deprecation -- Existing issue; this suppression was only added to allow existing lint warnings to be upgraded to errors.
) => JSX.Element;

const getPeriodFromDate = (dateString: string | null | undefined) => {
  if (!dateString) {
    return null;
  }

  const datePartMatch = /^(\d{4})-(\d{2})-(\d{2})/.exec(dateString.trim());
  if (datePartMatch) {
    const year = Number(datePartMatch[1]);
    const month = Number(datePartMatch[2]);
    if (month >= 1 && month <= 12) {
      return `${year}Q${Math.floor((month - 1) / 3) + 1}`;
    }
  }

  const date = new Date(dateString);
  if (Number.isNaN(date.getTime())) {
    return null;
  }

  return `${date.getUTCFullYear()}Q${Math.floor(date.getUTCMonth() / 3) + 1}`;
};

const getCurrentMetricsPeriod = () => `${CURRENT_YEAR}Q${CURRENT_QUARTER}`;

const parseMetricsPeriod = (period: string) => {
  const match = /^(\d{4})Q([1-4])$/.exec(period.trim());
  if (!match) {
    return null;
  }

  return {
    year: Number(match[1]),
    quarter: Number(match[2]),
  };
};

const compareMetricsPeriods = (a: string, b: string) => {
  const aParsed = parseMetricsPeriod(a);
  const bParsed = parseMetricsPeriod(b);
  if (!aParsed || !bParsed) {
    return a.localeCompare(b);
  }

  if (aParsed.year !== bParsed.year) {
    return aParsed.year - bParsed.year;
  }

  return aParsed.quarter - bParsed.quarter;
};

const getNextMetricsPeriod = (period: string) => {
  const parsed = parseMetricsPeriod(period);
  if (!parsed) {
    return period;
  }

  if (parsed.quarter === 4) {
    return `${parsed.year + 1}Q1`;
  }

  return `${parsed.year}Q${parsed.quarter + 1}`;
};

const buildMetricsPeriodRange = (start: string, end: string) => {
  const range: string[] = [];
  let current = start;

  while (compareMetricsPeriods(current, end) <= 0) {
    range.push(current);
    const next = getNextMetricsPeriod(current);
    if (next === current) {
      break;
    }
    current = next;
  }

  return range;
};

const getMetricsPeriodWithOffset = (period: string, offset: number) => {
  let nextPeriod = period;

  for (let index = 0; index < offset; index += 1) {
    nextPeriod = getNextMetricsPeriod(nextPeriod);
  }

  return nextPeriod;
};

const formatMetricPeriodChipLabel = (period: string) => {
  const parsed = parseMetricsPeriod(period);
  if (!parsed) {
    return period;
  }

  return `Q${parsed.quarter} ${String(parsed.year).slice(-2)}`;
};

const getSoonestMetricForecast = (metric: WorkstreamMetric) => {
  const forecasts = metric.metric_forecasts ?? [];
  if (!forecasts.length) {
    return undefined;
  }

  return forecasts.reduce((soonest, forecast) => {
    if (!soonest) {
      return forecast;
    }

    const soonestDate = soonest.forecast_date
      ? new Date(soonest.forecast_date).getTime()
      : Infinity;
    const forecastDate = forecast.forecast_date
      ? new Date(forecast.forecast_date).getTime()
      : Infinity;

    if (forecastDate !== soonestDate) {
      return forecastDate < soonestDate ? forecast : soonest;
    }

    return forecast.period < soonest.period ? forecast : soonest;
  }, forecasts[0]);
};

const getCurrentOrFutureMetricForecast = (metric: WorkstreamMetric) => {
  const currentPeriod = `${CURRENT_YEAR}Q${CURRENT_QUARTER}`;
  const eligibleForecasts = (metric.metric_forecasts ?? []).filter(
    (forecast) => forecast.period && forecast.period >= currentPeriod
  );

  if (!eligibleForecasts.length) {
    return undefined;
  }

  return eligibleForecasts.reduce((earliest, forecast) => {
    const earliestTime = earliest.forecast_date
      ? new Date(earliest.forecast_date).getTime()
      : Infinity;
    const forecastTime = forecast.forecast_date
      ? new Date(forecast.forecast_date).getTime()
      : Infinity;

    if (forecastTime !== earliestTime) {
      return forecastTime < earliestTime ? forecast : earliest;
    }

    return forecast.period < earliest.period ? forecast : earliest;
  });
};

const buildMetricForecastDrafts = (metric: WorkstreamMetric) => {
  const drafts = Object.fromEntries(
    (metric.metric_forecasts ?? []).map((forecast) => [
      forecast.period,
      {
        ...forecast,
        target: forecast.target ?? '',
        budget: forecast.budget ?? '',
        forecast_date: forecast.forecast_date ?? null,
      },
    ])
  ) as Record<
    string,
    {
      id?: number;
      period: string;
      target: string;
      budget: string;
      forecast_date: string | null;
      actual?: string | null;
    }
  >;

  const fallbackPeriod = getPeriodFromDate(getSoonestMetricDate(metric) ?? metric.complete_date);
  if (fallbackPeriod && !drafts[fallbackPeriod]) {
    drafts[fallbackPeriod] = {
      period: fallbackPeriod,
      target: getSoonestMetricTarget(metric) ?? metric.target ?? '',
      budget: '',
      forecast_date: getSoonestMetricDate(metric) ?? metric.complete_date ?? null,
    };
  }

  return drafts;
};

const normalizeMetricForecastDrafts = (
  drafts: Record<
    string,
    {
      id?: number;
      period: string;
      target: string;
      budget: string;
      forecast_date: string | null;
      actual?: string | null;
    }
  >,
  options?: {requireTargetOrBudget?: boolean}
) =>
  Object.values(drafts)
    .filter((forecast) =>
      options?.requireTargetOrBudget
        ? Boolean(forecast.target || forecast.budget)
        : Boolean(forecast.target || forecast.budget || forecast.forecast_date || forecast.id)
    )
    .map((forecast) => ({
      id: forecast.id ?? null,
      period: forecast.period,
      target: forecast.target ?? '',
      budget: forecast.budget ?? '',
      forecast_date: forecast.forecast_date ?? null,
    }))
    .sort((a, b) => compareMetricsPeriods(a.period, b.period));

const isDateWithinMetricsPeriod = (dateString: string | null | undefined, period: string) => {
  if (!dateString) {
    return false;
  }

  return getPeriodFromDate(dateString) === period;
};

const formatDate = (dateString: string | null) => {
  if (!dateString) {
    return null;
  }
  const date = new Date(dateString);
  return date.toLocaleString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });
};

export const WorkstreamOutcomeModify = ({
  isCreate,
  metric,
  workstreamOwner,
  isPending,
  onSave,
  onDelete,
  disableMetricComplete,
  disableMetricTypeChange,
  disableChangeReason,
  allowedMetricTypes,
  showMetricTypeSelector = false,
  workstreamId,
  metricPeriodOptions,
  createLabelKey = 'add_milestone',
  createTriggerMode = 'chip',
}: {
  isCreate?: boolean;
  metric: WorkstreamMetric;
  workstreamOwner?: EmployeeRoleLevel;
  isPending?: boolean;
  onSave?: (update: Partial<WorkstreamMetric>, reason?: string) => void;
  onDelete?: (reason: string) => Promise<void>;
  disableMetricComplete?: boolean;
  disableMetricTypeChange?: boolean;
  disableChangeReason?: boolean;
  allowedMetricTypes?: Array<'metric' | 'deliverable' | 'milestone'>;
  showMetricTypeSelector?: boolean;
  workstreamId: number;
  metricPeriodOptions?: string[];
  createLabelKey?: string;
  createTriggerMode?: 'chip' | 'icon' | 'button' | 'underline';
}) => {
  const user = useAtomValue(pulseUserAtom);
  const {data: draftPolicy} = useWorkstreamDraftPolicy({enabled: Boolean(user)});
  const draftReasonWaived = useMemo(
    () =>
      !isCreate &&
      isMetricDraftReasonWaived(
        draftPolicy,
        getMetricCreatedAtForDraftPolicy(metric, workstreamId)
      ),
    [draftPolicy, isCreate, metric, workstreamId]
  );
  const mergedDisableChangeReason = Boolean(disableChangeReason || draftReasonWaived);
  const {hasMnpiAccess} = useMnpiAccess();

  const currentMetricsPeriod = useMemo(() => getCurrentMetricsPeriod(), []);
  const showMetricPeriodSelector = Boolean(metricPeriodOptions?.length);
  const defaultMetricForecast = useMemo(
    () =>
      showMetricPeriodSelector
        ? getSoonestMetricForecast(metric)
        : (getCurrentOrFutureMetricForecast(metric) ?? getSoonestMetricForecast(metric)),
    [metric, showMetricPeriodSelector]
  );
  const initialMetricForecastDrafts = useMemo(() => buildMetricForecastDrafts(metric), [metric]);
  const derivedMetricPeriodOptions = useMemo(() => {
    if (metricPeriodOptions?.length) {
      return metricPeriodOptions;
    }

    const forecastPeriods = Object.keys(initialMetricForecastDrafts);
    if (forecastPeriods.length === 0) {
      return buildMetricsPeriodRange(
        currentMetricsPeriod,
        getMetricsPeriodWithOffset(currentMetricsPeriod, 2)
      );
    }

    const sortedForecastPeriods = [...forecastPeriods].sort(compareMetricsPeriods);
    const earliestForecastPeriod = sortedForecastPeriods[0];
    const latestForecastPeriod = sortedForecastPeriods[sortedForecastPeriods.length - 1];
    const minimumLatestPeriod = getMetricsPeriodWithOffset(currentMetricsPeriod, 2);
    const endPeriod =
      compareMetricsPeriods(latestForecastPeriod, minimumLatestPeriod) >= 0
        ? latestForecastPeriod
        : minimumLatestPeriod;
    const startPeriod =
      compareMetricsPeriods(currentMetricsPeriod, earliestForecastPeriod) <= 0
        ? currentMetricsPeriod
        : earliestForecastPeriod;

    return buildMetricsPeriodRange(startPeriod, endPeriod);
  }, [currentMetricsPeriod, initialMetricForecastDrafts, metricPeriodOptions]);
  const initialSelectedMetricPeriod = useMemo(() => {
    const defaultForecastPeriod =
      defaultMetricForecast?.period ??
      getPeriodFromDate(defaultMetricForecast?.forecast_date ?? null) ??
      getPeriodFromDate(getSoonestMetricDate(metric) ?? metric.complete_date) ??
      currentMetricsPeriod;
    return derivedMetricPeriodOptions.includes(defaultForecastPeriod)
      ? defaultForecastPeriod
      : (derivedMetricPeriodOptions[0] ?? currentMetricsPeriod);
  }, [currentMetricsPeriod, defaultMetricForecast, derivedMetricPeriodOptions, metric]);
  const [name, setName] = useState(metric.title);
  const [complete, setComplete] = useState(Boolean(metric.complete));
  const [type, setType] = useState(metric.type ?? 'deliverable');
  const [owner, setOwner] = useState<EmployeeRoleLevel | undefined>(metric.owner ?? undefined);
  const [selectedMetricPeriod, setSelectedMetricPeriod] = useState(initialSelectedMetricPeriod);
  const [metricForecastDrafts, setMetricForecastDrafts] = useState(initialMetricForecastDrafts);
  const [completeDate, setCompleteDate] = useState<string | null>(
    initialMetricForecastDrafts[initialSelectedMetricPeriod]?.forecast_date ??
      defaultMetricForecast?.forecast_date ??
      getSoonestMetricDate(metric) ??
      null
  );
  const [target, setTarget] = useState(
    initialMetricForecastDrafts[initialSelectedMetricPeriod]?.target ??
      defaultMetricForecast?.target ??
      getSoonestMetricTarget(metric) ??
      ''
  );
  const [budget, setBudget] = useState(
    initialMetricForecastDrafts[initialSelectedMetricPeriod]?.budget ??
      defaultMetricForecast?.budget ??
      ''
  );
  const [current, setCurrent] = useState(metric.current ?? '');
  const [documentUrl, setDocumentUrl] = useState(metric.url ?? '');
  const [isMnpi, setIsMnpi] = useState(Boolean(metric.is_mnpi));
  const [metricType, setMetricType] = useState(
    metric.metric_type ?? (showMetricTypeSelector ? 'other' : '')
  );
  const [isPrimary, setIsPrimary] = useState(isPrimaryMetric(metric, workstreamId));
  const [isDeleteMode, setIsDeleteMode] = useState(false);
  const [deleteReason, setDeleteReason] = useState('');
  const [editReason, setEditReason] = useState<string | undefined>(undefined);
  const isLongterm = isMetricLongterm(type);
  const displayType = type === 'deliverable' ? 'milestone' : type;
  const isPrimaryEligibleMetric =
    type === 'metric' && (metricType === 'leading' || metricType === 'lagging');
  const typeOptions = useMemo<Array<'metric' | 'milestone'>>(() => {
    if (allowedMetricTypes?.length) {
      return Array.from(
        new Set(
          allowedMetricTypes.map((allowedType) =>
            allowedType === 'deliverable' ? 'milestone' : allowedType
          )
        )
      );
    }

    return isLongterm ? ['milestone', 'metric'] : ['milestone'];
  }, [allowedMetricTypes, isLongterm]);
  const canChangeType = !disableMetricTypeChange && typeOptions.length > 1;
  const metricTypeTriggerLabel =
    metricType === 'leading'
      ? t('lead').toString().toUpperCase()
      : metricType === 'lagging'
        ? t('lag').toString().toUpperCase()
        : '';

  const [popoverOpen, setPopoverOpen] = useState(false);
  const [hasStartedSave, setHasStartedSave] = useState(false);
  const [wasPending, setWasPending] = useState(false);

  const popoverContentRef = useRef<HTMLDivElement>(null);

  const needsRequiredFields = useMemo(() => {
    if (type === 'deliverable' && complete) {
      return !documentUrl;
    }

    if (type === 'metric') {
      return !current || (!showMetricTypeSelector && !target);
    }

    return !name;
  }, [type, name, complete, documentUrl, current, showMetricTypeSelector, target]);
  const metricEtaError = useMemo(
    () =>
      type === 'metric' &&
      showMetricPeriodSelector &&
      !isDateWithinMetricsPeriod(completeDate, selectedMetricPeriod),
    [completeDate, selectedMetricPeriod, showMetricPeriodSelector, type]
  );
  const existingMetricForecastEdited = useMemo(() => {
    if (type !== 'metric') {
      return false;
    }

    const existingForecastsByPeriod = Object.fromEntries(
      (metric.metric_forecasts ?? []).map((forecast) => [forecast.period, forecast])
    );

    return Object.entries(existingForecastsByPeriod).some(([period, forecast]) => {
      const draft = metricForecastDrafts[period];
      if (!draft) {
        return false;
      }

      return (
        (draft.target ?? '') !== (forecast.target ?? '') ||
        (draft.budget ?? '') !== (forecast.budget ?? '') ||
        (draft.forecast_date ?? null) !== (forecast.forecast_date ?? null)
      );
    });
  }, [metric.metric_forecasts, metricForecastDrafts, type]);

  const changedRequiredField = useMemo(() => {
    if (isCreate || mergedDisableChangeReason) return false;

    if (
      (metric.title &&
        parseAndExtractTextFromLexicalJson(name) !==
          parseAndExtractTextFromLexicalJson(metric.title)) ||
      (metric.type && type !== metric.type) ||
      (type === 'metric' && metric.metric_type !== metricType) ||
      (type === 'metric' &&
        isPrimaryMetric(metric, workstreamId) !== (isPrimaryEligibleMetric ? isPrimary : false)) ||
      existingMetricForecastEdited ||
      (metric.url && documentUrl !== metric.url) ||
      (metric.owner && owner?.ldap !== metric.owner?.ldap) ||
      (type !== 'metric' &&
        getSoonestMetricDate(metric) &&
        formatDate(completeDate) !== formatDate(getSoonestMetricDate(metric)))
    ) {
      return true;
    }

    return false;
  }, [
    isCreate,
    metric,
    name,
    type,
    metricType,
    isPrimary,
    isPrimaryEligibleMetric,
    documentUrl,
    owner,
    completeDate,
    mergedDisableChangeReason,
    existingMetricForecastEdited,
    workstreamId,
  ]);

  const updateSelectedMetricForecast = useCallback(
    (
      updates: Partial<{
        target: string;
        budget: string;
        forecast_date: string | null;
      }>
    ) => {
      setMetricForecastDrafts((previous) => ({
        ...previous,
        [selectedMetricPeriod]: {
          ...previous[selectedMetricPeriod],
          period: selectedMetricPeriod,
          target: previous[selectedMetricPeriod]?.target ?? '',
          budget: previous[selectedMetricPeriod]?.budget ?? '',
          forecast_date: previous[selectedMetricPeriod]?.forecast_date ?? null,
          ...updates,
        },
      }));
    },
    [selectedMetricPeriod]
  );

  const resetState = useCallback(() => {
    setName(metric.title);
    setComplete(Boolean(metric.complete));
    setType(metric.type ?? 'deliverable');
    setOwner(metric.owner ?? undefined);
    setSelectedMetricPeriod(initialSelectedMetricPeriod);
    setMetricForecastDrafts(initialMetricForecastDrafts);
    setCompleteDate(
      initialMetricForecastDrafts[initialSelectedMetricPeriod]?.forecast_date ??
        defaultMetricForecast?.forecast_date ??
        getSoonestMetricDate(metric) ??
        null
    );
    setTarget(
      initialMetricForecastDrafts[initialSelectedMetricPeriod]?.target ??
        defaultMetricForecast?.target ??
        getSoonestMetricTarget(metric) ??
        ''
    );
    setBudget(
      initialMetricForecastDrafts[initialSelectedMetricPeriod]?.budget ??
        defaultMetricForecast?.budget ??
        ''
    );
    setCurrent(metric.current ?? '');
    setDocumentUrl(metric.url ?? '');
    setIsMnpi(Boolean(metric.is_mnpi));
    setMetricType(metric.metric_type ?? (showMetricTypeSelector ? 'other' : ''));
    setIsPrimary(isPrimaryMetric(metric, workstreamId));
  }, [
    defaultMetricForecast,
    initialMetricForecastDrafts,
    initialSelectedMetricPeriod,
    metric,
    workstreamId,
    showMetricTypeSelector,
  ]);

  const closePopup = useCallback(() => {
    resetState();
    setPopoverOpen(false);
    setIsDeleteMode(false);
    setDeleteReason('');
  }, [resetState]);

  // Use outside click hook to close the portal
  useOutsideClick(popoverContentRef, closePopup, [], true, 'mousedown');

  const handleSave = () => {
    setHasStartedSave(true);
    const metricPeriod = type === 'metric' ? getPeriodFromDate(completeDate) : null;
    const normalizedMetricForecasts = normalizeMetricForecastDrafts(metricForecastDrafts, {
      requireTargetOrBudget: type === 'metric' && showMetricTypeSelector,
    });
    const selectedMetricForecastHasValues = Boolean(target || budget);
    const updatedForecasts =
      type === 'metric' && metricPeriod
        ? normalizedMetricForecasts.map((forecast) => ({
            ...forecast,
            budget: forecast.budget || null,
          }))
        : metric.metric_forecasts;
    const metricLink = getMetricWorkstreamLink(metric, workstreamId);
    const payload = {
      title: name,
      complete,
      complete_date:
        type === 'metric' && showMetricTypeSelector && !selectedMetricForecastHasValues
          ? null
          : completeDate,
      owner,
      type,
      target:
        type === 'metric' && showMetricTypeSelector && !selectedMetricForecastHasValues
          ? ''
          : target,
      current,
      url: documentUrl,
      is_longterm: isLongterm,
      metric_type:
        type === 'metric' && showMetricTypeSelector ? metricType || null : metric.metric_type,
      workstream_metric_link: {
        ...(metricLink?.id != null ? {id: metricLink.id} : {}),
        ...(metricLink?.id != null ? {order: getMetricOrder(metric, workstreamId)} : {}),
        is_primary:
          type === 'metric' && showMetricTypeSelector
            ? isPrimaryEligibleMetric
              ? isPrimary
              : false
            : isPrimaryMetric(metric, workstreamId),
      } as WorkstreamMetricWithCurrentLink['workstream_metric_link'],
      metric_forecasts: updatedForecasts as WorkstreamMetric['metric_forecasts'],
      is_mnpi: hasMnpiAccess ? isMnpi : metric.is_mnpi, // Only update if user has access
    } as Partial<WorkstreamMetricWithCurrentLink>;
    onSave?.(payload as Partial<WorkstreamMetric>, editReason);
  };

  const handleDelete = async () => {
    setHasStartedSave(true);
    await onDelete?.(deleteReason);
  };

  useEffect(() => {
    // Track when isPending becomes true after starting a save
    if (hasStartedSave && isPending) {
      setWasPending(true);
    }

    // Only close popover if we've started a save operation, isPending was true, and now it's false
    if (hasStartedSave && wasPending && !isPending) {
      closePopup();
      setHasStartedSave(false);
      setWasPending(false);
    }
  }, [closePopup, isPending, hasStartedSave, wasPending]);

  // Reset edit reason when modal opens
  useEffect(() => {
    if (type !== 'metric') {
      return;
    }

    const selectedDraft = metricForecastDrafts[selectedMetricPeriod];
    const nextTarget = selectedDraft?.target ?? '';
    const nextBudget = selectedDraft?.budget ?? '';
    const nextCompleteDate = selectedDraft?.forecast_date ?? null;

    if (target !== nextTarget) {
      setTarget(nextTarget);
    }
    if (budget !== nextBudget) {
      setBudget(nextBudget);
    }
    if (completeDate !== nextCompleteDate) {
      setCompleteDate(nextCompleteDate);
    }
  }, [budget, completeDate, metricForecastDrafts, selectedMetricPeriod, target, type]);

  useEffect(() => {
    if (!isPrimaryEligibleMetric && isPrimary) {
      setIsPrimary(false);
    }
  }, [isPrimary, isPrimaryEligibleMetric]);

  useEffect(() => {
    if (popoverOpen) {
      setEditReason(undefined);
      setHasStartedSave(false);
      setWasPending(false);
      setIsDeleteMode(false);
      setDeleteReason('');
    }
  }, [popoverOpen]);

  useEffect(() => {
    if (!popoverOpen) {
      return;
    }

    const focusTitleInput = () => {
      const titleEditorElement = popoverContentRef.current?.querySelector<HTMLElement>(
        '[contenteditable="true"]'
      );
      titleEditorElement?.focus();
    };

    focusTitleInput();
    const animationFrameId = window.requestAnimationFrame(focusTitleInput);
    const timeoutId = window.setTimeout(focusTitleInput, 50);

    return () => {
      window.cancelAnimationFrame(animationFrameId);
      window.clearTimeout(timeoutId);
    };
  }, [popoverOpen]);

  const handlePopoverToggle = useCallback(
    (nextOpen: boolean) => {
      if (nextOpen) {
        resetState();
      }
      setPopoverOpen(nextOpen);
    },
    [resetState]
  );

  return (
    <>
      <Popover
        open={popoverOpen}
        onToggle={handlePopoverToggle}
        placement={isCreate ? 'bottom-start' : 'right-start'}
        offsetDistance={isCreate ? 4 : -130}
        shouldFocusContentOnOpen={false}
      >
        <Popover.Trigger asChild>
          {isCreate ? (
            createTriggerMode === 'icon' ? (
              <Box display="inline-flex">
                <Tooltip title={t(createLabelKey).toString()} placement="right">
                  <IconButton
                    variant="outline"
                    size="small"
                    aria-label={t(createLabelKey)}
                    data-outcome-modify-open={popoverOpen ? 'true' : 'false'}
                  >
                    <UIIcon src={AddLine} className={atoms({color: 'Border Base'})} />
                  </IconButton>
                </Tooltip>
              </Box>
            ) : createTriggerMode === 'button' ? (
              <Button
                variant="outline"
                size="small"
                data-outcome-modify-open={popoverOpen ? 'true' : 'false'}
              >
                {t(createLabelKey)}
              </Button>
            ) : createTriggerMode === 'underline' ? (
              <button
                type="button"
                data-outcome-modify-open={popoverOpen ? 'true' : 'false'}
                style={{
                  appearance: 'none',
                  border: 'none',
                  background: 'transparent',
                  padding: 0,
                  margin: 0,
                  color: 'var(--dig-color__text__base)',
                  fontFamily: 'var(--type__body__standard--fontfamily)',
                  fontSize: 'var(--type__body__xsmall--fontsize)',
                  fontWeight: 500,
                  lineHeight: 'var(--dig-type__lineheight__label__xsmall)',
                  textDecoration: 'underline',
                  textUnderlineOffset: '2px',
                  textDecorationThickness: '1px',
                  textDecorationColor: 'var(--dig-color__border__bold)',
                  cursor: 'pointer',
                }}
              >
                {t(createLabelKey)}
              </button>
            ) : (
              <Chip
                data-outcome-modify-open={popoverOpen ? 'true' : 'false'}
                size="small"
                className={atoms({
                  backgroundColor: 'transparent',
                  borderColor: 'Border Subtle',
                  borderStyle: 'Solid',
                  borderWidth: '1',
                })}
              >
                <Chip.IconAccessory>
                  <UIIcon src={AddLine} className={atoms({color: 'Border Base'})} />
                </Chip.IconAccessory>
                <Chip.Content>
                  <Text color="subtle" size="small">
                    {t(createLabelKey)}
                  </Text>
                </Chip.Content>
              </Chip>
            )
          ) : (
            <Button
              variant="transparent"
              size="small"
              data-outcome-modify-open={popoverOpen ? 'true' : 'false'}
            >
              <Text color="subtle" size="xsmall">
                {t('edit')}
              </Text>
            </Button>
          )}
        </Popover.Trigger>
        <Popover.Overlay>
          <PopoverContent ref={popoverContentRef}>
            <Popover.Body>
              <Split gap="6" style={{minWidth: 628}} paddingX="12" paddingY="8">
                {!disableMetricComplete && (
                  <Split.Item width="hug">
                    <Box marginTop="4">
                      <Checkbox checked={complete} onChange={() => setComplete(!complete)} />
                    </Box>
                  </Split.Item>
                )}
                <Split.Item width="fill">
                  <Stack gap="10">
                    <Split
                      width="100%"
                      paddingBottom="8"
                      className={atoms({borderBottom: 'Solid', borderColor: 'Border Subtle'})}
                      gap="10"
                    >
                      {type === 'metric' && showMetricTypeSelector ? (
                        <Split.Item width="hug">
                          <Menu.Wrapper
                            onSelection={(selection) => setMetricType(selection)}
                            shouldPropagateClickOutsideMouseEvents
                          >
                            {({getContentProps, getTriggerProps}) => (
                              <>
                                <Chip
                                  {...getTriggerProps()}
                                  size="small"
                                  className={atoms({backgroundColor: 'Background Subtle'})}
                                >
                                  {metricTypeTriggerLabel ? (
                                    <Chip.Content>
                                      <Text size="xsmall" color="subtle">
                                        {metricTypeTriggerLabel}
                                      </Text>
                                    </Chip.Content>
                                  ) : null}
                                  <Chip.IconAccessory>
                                    <UIIcon src={ChevronDownLine} size="small" />
                                  </Chip.IconAccessory>
                                </Chip>
                                <Menu.Content
                                  {...getContentProps()}
                                  minWidth="200px"
                                  placement="bottom-start"
                                >
                                  <Menu.Segment>
                                    {(['leading', 'lagging'] as const).map((option) => (
                                      <Menu.ActionItem
                                        key={option}
                                        onClick={() => setMetricType(option)}
                                        withRightAccessory={
                                          metricType === option ? (
                                            <UIIcon src={CheckmarkLine} />
                                          ) : undefined
                                        }
                                      >
                                        {t(option)}
                                      </Menu.ActionItem>
                                    ))}
                                  </Menu.Segment>
                                  <Menu.Segment>
                                    <Menu.ActionItem
                                      onClick={() => setMetricType('other')}
                                      withRightAccessory={
                                        metricType === 'other' ? (
                                          <UIIcon src={CheckmarkLine} />
                                        ) : undefined
                                      }
                                    >
                                      {t('other')}
                                    </Menu.ActionItem>
                                  </Menu.Segment>
                                </Menu.Content>
                              </>
                            )}
                          </Menu.Wrapper>
                        </Split.Item>
                      ) : null}
                      <Split.Item width="fill">
                        <Box
                          className={atoms({
                            borderStyle: 'Solid',
                            borderColor: 'Border Subtle',
                            borderRadius: 'Small',
                            paddingTop: '4',
                          })}
                        >
                          <RichTextArea
                            placeholder={t('start_writing_placeholder')}
                            value={name}
                            onChange={(value) => setName(value?.toString() || '')}
                            autoFocus
                            editable
                            hideCommentHighlights
                            hideToolbar
                            disableBorder
                            minHeight={26}
                            plainText
                          />
                        </Box>
                      </Split.Item>
                      <Split.Item width="hug">
                        <Split gap="8">
                          <Split.Item>
                            <CancelButton
                              disabled={hasStartedSave}
                              onClick={
                                isDeleteMode
                                  ? () => {
                                      setIsDeleteMode(false);
                                      setDeleteReason('');
                                    }
                                  : closePopup
                              }
                            >
                              {t('cancel')}
                            </CancelButton>
                          </Split.Item>
                          <Split.Item>
                            {isDeleteMode ? (
                              <Button
                                isLoading={hasStartedSave}
                                onClick={handleDelete}
                                variant="primary"
                                disabled={!draftReasonWaived && !deleteReason}
                              >
                                {t('delete')}
                              </Button>
                            ) : (
                              <Button
                                isLoading={hasStartedSave}
                                onClick={handleSave}
                                variant="primary"
                                disabled={
                                  (changedRequiredField && !editReason) ||
                                  needsRequiredFields ||
                                  !completeDate ||
                                  metricEtaError
                                }
                              >
                                {t('save')}
                              </Button>
                            )}
                          </Split.Item>
                        </Split>
                      </Split.Item>
                    </Split>

                    {!isDeleteMode && (
                      <Split width="100%" alignY="center" style={{minHeight: 32}}>
                        <Split.Item>
                          <Menu.Wrapper
                            onSelection={(selection) => setType(selection)}
                            shouldPropagateClickOutsideMouseEvents
                          >
                            {({getContentProps, getTriggerProps}) => (
                              <>
                                <Chip
                                  {...getTriggerProps()}
                                  size="small"
                                  disabled={!canChangeType}
                                  onClick={canChangeType ? getTriggerProps().onClick : undefined}
                                  withDropdownIcon={canChangeType}
                                >
                                  <Chip.IconAccessory>
                                    <UIIcon
                                      src={
                                        displayType === 'milestone' ? MilestoneLine : MetricsLine
                                      }
                                      className={atoms({color: 'Text Subtle'})}
                                    />
                                  </Chip.IconAccessory>
                                  <Chip.Content>{t(displayType)}</Chip.Content>
                                </Chip>

                                <Menu.Content
                                  {...getContentProps()}
                                  minWidth="200px"
                                  placement="bottom-start"
                                >
                                  <Menu.Segment>
                                    {typeOptions.includes('milestone') ? (
                                      <Menu.SelectItem
                                        selected={displayType === 'milestone'}
                                        withRightAccessory={
                                          displayType === 'milestone' && (
                                            <UIIcon src={CheckmarkLine} />
                                          )
                                        }
                                        value="milestone"
                                        withLeftAccessory={<UIIcon src={MilestoneLine} />}
                                      >
                                        {t('milestone')}
                                      </Menu.SelectItem>
                                    ) : null}
                                    {typeOptions.includes('metric') ? (
                                      <Menu.SelectItem
                                        selected={type === 'metric'}
                                        withRightAccessory={
                                          type === 'metric' && <UIIcon src={CheckmarkLine} />
                                        }
                                        value="metric"
                                        withLeftAccessory={<UIIcon src={MetricsLine} />}
                                      >
                                        {t('metric')}
                                      </Menu.SelectItem>
                                    ) : null}
                                  </Menu.Segment>
                                </Menu.Content>
                              </>
                            )}
                          </Menu.Wrapper>
                        </Split.Item>

                        {type === 'metric' && (
                          <>
                            {showMetricPeriodSelector ? (
                              <Split.Item>
                                <Menu.Wrapper
                                  onSelection={(selection) => setSelectedMetricPeriod(selection)}
                                  shouldPropagateClickOutsideMouseEvents
                                >
                                  {({getContentProps, getTriggerProps}) => (
                                    <>
                                      <Chip
                                        {...getTriggerProps()}
                                        size="small"
                                        withDropdownIcon
                                        className={atoms({backgroundColor: 'Background Subtle'})}
                                      >
                                        <Chip.Content>
                                          {formatMetricPeriodChipLabel(selectedMetricPeriod)}
                                        </Chip.Content>
                                      </Chip>
                                      <Menu.Content
                                        {...getContentProps()}
                                        minWidth="120px"
                                        placement="bottom-start"
                                      >
                                        <Menu.Segment>
                                          {derivedMetricPeriodOptions.map((period) => (
                                            <Menu.ActionItem
                                              key={period}
                                              onClick={() => setSelectedMetricPeriod(period)}
                                              withRightAccessory={
                                                selectedMetricPeriod === period ? (
                                                  <UIIcon src={CheckmarkLine} />
                                                ) : undefined
                                              }
                                            >
                                              {period}
                                            </Menu.ActionItem>
                                          ))}
                                        </Menu.Segment>
                                      </Menu.Content>
                                    </>
                                  )}
                                </Menu.Wrapper>
                              </Split.Item>
                            ) : null}
                            <Split.Item>
                              <TextInput
                                style={{width: 64}}
                                placeholder={t('current')}
                                value={current}
                                onChange={(e) => setCurrent(e.target.value)}
                              />
                            </Split.Item>
                            <Split.Item>
                              <TextInput
                                placeholder={t('target')}
                                style={{width: 64}}
                                value={target}
                                onChange={(e) => {
                                  setTarget(e.target.value);
                                  updateSelectedMetricForecast({target: e.target.value});
                                }}
                              />
                            </Split.Item>
                            {showMetricTypeSelector ? (
                              <Split.Item>
                                <TextInput
                                  placeholder={t('budget')}
                                  style={{width: 64}}
                                  value={budget}
                                  onChange={(e) => {
                                    setBudget(e.target.value);
                                    updateSelectedMetricForecast({budget: e.target.value});
                                  }}
                                />
                              </Split.Item>
                            ) : null}
                            {showMetricTypeSelector && isPrimaryEligibleMetric ? (
                              <Split.Item>
                                <Box
                                  display="flex"
                                  alignItems="center"
                                  style={{gap: 8}}
                                  className={atoms({
                                    paddingX: '8',
                                    paddingY: '4',
                                    borderRadius: 'Small',
                                    backgroundColor: 'Background Subtle',
                                  })}
                                >
                                  <Checkbox
                                    aria-label="Primary metric"
                                    checked={isPrimary}
                                    onChange={() => setIsPrimary(!isPrimary)}
                                  />
                                  <Text size="xsmall" color="subtle">
                                    Primary
                                  </Text>
                                </Box>
                              </Split.Item>
                            ) : null}
                          </>
                        )}

                        {type === 'deliverable' && complete && (
                          <Split.Item>
                            <TextInput
                              placeholder={t('milestone_url_placeholder')}
                              style={{width: 128}}
                              value={documentUrl}
                              onChange={(e) => setDocumentUrl(e.target.value)}
                            />
                          </Split.Item>
                        )}

                        {type !== 'milestone' && (
                          <Split.Item>
                            <Box
                              style={{width: 1, height: 24}}
                              marginLeft="4"
                              paddingLeft="4"
                              borderLeft="Solid"
                              borderColor="Border Subtle"
                            />
                          </Split.Item>
                        )}

                        <Split.Item>
                          <EmployeeSelect
                            selection={owner ?? workstreamOwner}
                            setSelection={(employee) => {
                              setOwner(employee);
                            }}
                          />
                        </Split.Item>

                        <Split.Item>
                          <DatePickerEditor
                            date={completeDate}
                            setDate={(date) => {
                              setCompleteDate(date);
                              if (type === 'metric') {
                                updateSelectedMetricForecast({forecast_date: date});
                              }
                            }}
                            error={type === 'metric' ? metricEtaError : !completeDate}
                          />
                        </Split.Item>

                        {isUserAdmin(user) && hasMnpiAccess && type === 'metric' && (
                          <>
                            <Split.Item>
                              <Box
                                style={{width: 1, height: 24}}
                                marginLeft="4"
                                paddingLeft="4"
                                borderLeft="Solid"
                                borderColor="Border Subtle"
                              />
                            </Split.Item>

                            <Split.Item>
                              <Chip
                                size="small"
                                isSelected={isMnpi}
                                onClick={() => setIsMnpi(!isMnpi)}
                                className={atoms({cursor: 'pointer'})}
                              >
                                <Chip.IconAccessory>
                                  <UIIcon src={LockLine} />
                                </Chip.IconAccessory>
                                <Chip.Content>{t('mnpi')}</Chip.Content>
                              </Chip>
                            </Split.Item>
                          </>
                        )}

                        {!isCreate && onDelete && (
                          <>
                            <Split.Item>
                              <Box
                                style={{width: 1, height: 24}}
                                marginLeft="4"
                                paddingLeft="4"
                                borderLeft="Solid"
                                borderColor="Border Subtle"
                              />
                            </Split.Item>

                            <Split.Item>
                              <IconButton
                                size="small"
                                variant="outline"
                                onClick={() => setIsDeleteMode(true)}
                                aria-label="icon button"
                              >
                                <UIIcon src={DeleteLine} />
                              </IconButton>
                            </Split.Item>
                          </>
                        )}
                      </Split>
                    )}

                    {isDeleteMode && (
                      <TextInput
                        withLeftAccessory={<UIIcon src={DeleteLine} />}
                        placeholder={t('outcome_delete_reason')}
                        value={deleteReason}
                        isInvalid={!draftReasonWaived && !deleteReason}
                        onChange={(e) => setDeleteReason(e.target.value)}
                      />
                    )}

                    {changedRequiredField && !isDeleteMode && (
                      <TextInput
                        withLeftAccessory={<UIIcon src={EditLine} />}
                        isInvalid={!editReason}
                        placeholder={t('outcome_edit_reason')}
                        value={editReason}
                        onChange={(e) => setEditReason(e.target.value)}
                      />
                    )}
                  </Stack>
                </Split.Item>
              </Split>
            </Popover.Body>
          </PopoverContent>
        </Popover.Overlay>
      </Popover>
    </>
  );
};
